import _ from "underscore";
import { startsWith, parseUrl } from "util/string-utils";
import platform from "platform";
import { isProdEnvironment, isDevEnvironment } from "util/env-utils";

const BrowserUtils = {
    constants: {
        unsupportedExceptions: [`unsupported`, `analytics`]
    },

    isBrowserSupported: function (pageName: string) {
        const tests = [];

        tests.push(this.isMediaSourceExtensionsEnabled);
        tests.push(this.isCanvasEnabled);
        tests.push(this.isHTML5VideoEnabled);
        tests.push(this.isFlexboxEnabled);

        if (pageName === `trim.jsp` || pageName === `editor.jsp`) {
            tests.push(_.negate(this.isSafari));
        }

        if (pageName === `producer`) {
            tests.push(_.negate(this.isFirefox));
            tests.push(_.negate(this.isSafari));
            tests.push(_.negate(this.isEdge));
            tests.push(this.isFetchEnabled);
        }

        return _.every(tests, function (test) {
            return test();
        });
    },

    isFetchEnabled: () => {
        return window && window.fetch ? true : false;
    },

    isMediaSourceExtensionsEnabled: function () {
        return window && window.MediaSource ? true : false;
    },

    isCanvasEnabled: function () {
        const canvas = document.createElement(`canvas`);
        return canvas.getContext ? true : false;
    },

    isHTML5VideoEnabled: function () {
        const video = document.createElement(`video`);
        return video.canPlayType ? true : false;
    },

    isFlexboxEnabled: function () {
        if (
            Object.prototype.hasOwnProperty.call(window, `CSS`) &&
            window.CSS.supports
        ) {
            const values = [
                `-webkit-box`,
                `-webkit-flex`,
                `-ms-flexbox`,
                `flex`
            ];
            const flexboxSupportPredicate = function (value) {
                return CSS.supports(`display`, value);
            };

            return _.some(values, flexboxSupportPredicate);
        } else {
            // No support for this in IE, we fallback to UA testing
            // IE supports flexbox from IE 10.
            // We are using IE conditionnal comments to add a flag to test if it's IE9 and below

            // Apart IE, CanIUse says that everything supports flexbox
            if (window.legacyIE) {
                return false;
            } else {
                return true;
            }
        }
    },

    // User agent sniffing is bad, you should not do that!
    // But in this case Safari pretends to support crossOrigin when it still
    // has its bloody CORS issue.
    // So Apple, you leave me no choice...
    isSafari: function () {
        const chrome = navigator.userAgent.indexOf(`Chrome`) > -1;
        let safari = navigator.userAgent.indexOf(`Safari`) > -1;
        if (chrome && safari) {
            safari = false;
        }
        return safari;
    },

    isFirefox: function () {
        return navigator.userAgent.toLowerCase().includes(`firefox`);
    },

    isEdge: function () {
        return navigator.userAgent.toLowerCase().includes(`edge`);
    },

    /**
     * Uses userAgentData.brands as there is a movement away from exposing data in
     * user agent string
     *
     * https://web.dev/user-agent-client-hints/
     * https://wicg.github.io/ua-client-hints/
     */
    isProducerSupportedBrowser: () => {
        const supportedBrowsers = [`Google Chrome`, `Microsoft Edge`];
        const isSupported = ({ brand }) => supportedBrowsers.includes(brand);
        return (
            //@ts-ignore: see above
            window.navigator.userAgentData?.brands?.some(isSupported) ||
            window.navigator.userAgent?.includes(`Grabyo Test`)
        );
    },

    redirectUnsupportedBrowser: function (currentPage: string) {
        if (this.isBrowserSupported(currentPage)) {
            return;
        }

        const allowedPage = _.find(
            this.constants.unsupportedExceptions,
            function (page) {
                return currentPage.indexOf(page) >= 0;
            }
        );

        if (_.isUndefined(allowedPage)) {
            window.location = `unsupported.jsp` as string & Location;
        }
    },

    isRtmpUrl(url = ``) {
        if (_.isString(url)) {
            return startsWith(url, /rtmpe?:\/\//);
        } else {
            return false;
        }
    },

    isHlsUrl(url = ``) {
        const { pathname } = parseUrl(url);
        return pathname.indexOf(`.m3u`) > -1;
    },

    isHttpMixedContentRequest(url = ``) {
        if (location.protocol === `https:` && _.isString(url)) {
            return startsWith(url, `http://`);
        } else {
            return false;
        }
    },

    getBaseUrl(options: { insecure?: boolean }) {
        if (!_.isBoolean(isDevEnvironment) || !_.isBoolean(isProdEnvironment)) {
            console.warn(
                `Browser utils - getBaseUrl(): Not all environment variables set. Unexpected results may occur.`
            );
        }
        if (!window.data) {
            return new Error(`window.data must be defined`);
        }
        if (isDevEnvironment) {
            return `${window.location.origin}/grabyo/studio`;
        }
        if (isProdEnvironment) {
            if (options.insecure) {
                // Hmmm... not ideal, but should do the trick for prod for now
                return `http://grabyo.com/studio`;
            } else {
                return window.location.origin;
            }
        }
        console.error(
            `Browser utils - getBaseUrl(): Correct environment variable not defined! Returning origin.`
        );
        return window.location.origin;
    },

    emptySelection() {
        window.getSelection().empty();
    }
};

export const userOS = platform.os;
export const isWindows = platform.os.family?.includes(`Windows`);
export const isMac = platform.os.family?.includes(`OS X`);

// metaKey = cmd on mac os, windows key on windows so allow ctrl or metaKey
export const systemModifierKey = isMac ? `metaKey` : `ctrlKey`;
export const systemModifierKeyDisplayString = isMac ? `⌘` : `ctrl`;
export const systemAltKeyDisplayString = isMac ? `⌥` : `alt`;

// Other cross browser support checks
export const AudioContext =
    typeof window !== `undefined` &&
    //@ts-ignore: feature detection
    (window.AudioContext || window.webkitAudioContext);
export const isServer = () => typeof window === `undefined`;
export default BrowserUtils;
